import { createAsyncThunk } from '@reduxjs/toolkit';
import { post } from 'aws-amplify/api';
import {
	RHYME_API_SERVICES,
	DraftModel,
	StandardResponse,
	Referral,
	SdgSubmit,
} from 'type-declarations';
import { sdgService_ValidateSdg } from './validate-thunks';

const rhymeApiUrl = RHYME_API_SERVICES.RhymeApi;

async function draftService_SubmitDraft(draft: DraftModel): Promise<StandardResponse> {
	const path = `organization/${draft.organizationId}/draft/${draft.partnerCaseId}/submit?notifyOnError=true`;

	const options = {
		body: { ...draft.referral } as unknown as FormData,
	};

	const { body } = await post({
		apiName: rhymeApiUrl,
		path: path,
		options,
	}).response;
	return (await body.json()) as unknown as StandardResponse;
}

export const submitDraft = createAsyncThunk(
	'transaction-thunk/submit-draft',
	async (draft: DraftModel) => {
		const response = await draftService_SubmitDraft(draft);
		return response;
	}
);

async function caseService_SubmitCase(
	orgId: string,
	partnerCaseId: string,
	benefitManagerId: string,
	referral: Referral
): Promise<StandardResponse> {
	const path = `organization/${orgId}/case?partnerCaseId=${partnerCaseId}&validation=false`;

	const options = {
		body: {
			benefitManagerId: benefitManagerId,
			referral: referral,
		} as unknown as FormData,
	};

	const { body } = await post({
		apiName: rhymeApiUrl,
		path: path,
		options,
	}).response;
	return (await body.json()) as unknown as StandardResponse;
}

export const submitCase = createAsyncThunk(
	'transaction-thunk/submit-case',
	async ({
		orgId,
		partnerCaseId,
		benefitManagerId,
		referral,
	}: {
		orgId: string;
		partnerCaseId: string;
		benefitManagerId: string;
		referral: Referral;
	}) => {
		const response = await caseService_SubmitCase(orgId, partnerCaseId, benefitManagerId, referral);
		return response;
	}
);

async function sdgService_SubmitSdg(
	orgId: string,
	caseId: string,
	submissionId: string,
	submitData: SdgSubmit
): Promise<number> {
	const path = `organization/${orgId}/case/${caseId}/submission/${submissionId}/data-gathering/submit`;

	const options = {
		body: submitData as unknown as FormData,
	};

	const { statusCode } = await post({
		apiName: rhymeApiUrl,
		path: path,
		options,
	}).response;
	return statusCode;
}

export const submitSdgData = createAsyncThunk(
	'transaction-thunk/submit-sdg',
	async ({
		orgId,
		caseId,
		submissionId,
		submitData,
	}: {
		orgId: string;
		caseId: string;
		submissionId: string;
		submitData: SdgSubmit;
	}) => {
		return await sdgService_SubmitSdg(orgId, caseId, submissionId, submitData);
	}
);

export const validateAndSubmitSdg = createAsyncThunk(
	'transaction-thunk/validate-submit-sdg',
	async ({
		orgId,
		caseId,
		submissionId,
		submitData,
	}: {
		orgId: string;
		caseId: string;
		submissionId: string;
		submitData: SdgSubmit;
	}) => {
		const validationResponse = await sdgService_ValidateSdg(
			orgId,
			caseId,
			submissionId,
			submitData.submittedData
		);
		if (!validationResponse) sdgService_SubmitSdg(orgId, caseId, submissionId, submitData);
	}
);

async function sdgService_SubmitSdgIncludeScreen(
	orgId: string,
	caseId: string,
	submitData: SdgSubmit,
	submissionId: string,
	screen: string
): Promise<number> {
	const path = `organization/${orgId}/case/${caseId}/submission/${submissionId}/data-gathering/submit`;

	const options = {
		body: { DataGatheringScreen: screen, SubmittedData: submitData } as unknown as FormData,
	};

	//TODO: not sure if we want to return statusCode
	const { statusCode } = await post({
		apiName: rhymeApiUrl,
		path: path,
		options,
	}).response;

	return statusCode;
}

export const submitSdgIncludeScreen = createAsyncThunk(
	'transaction-thunk/submit-sdg-includes-screen',
	async ({
		orgId,
		caseId,
		submitData,
		submissionId,
		screen,
	}: {
		orgId: string;
		caseId: string;
		submitData: SdgSubmit;
		submissionId: string;
		screen: string;
	}) => {
		const response = sdgService_SubmitSdgIncludeScreen(
			orgId,
			caseId,
			submitData,
			submissionId,
			screen
		);
		return response;
	}
);
